import React, { useState, useEffect } from "react";
import { useAppSelector } from "../../../../context-store";
import IdNameModel from "../../../../models/common/IdNameModel";
import toasterUtils from "../../../../utils/toaster-utils";
import AllProfilesComponent from "../../../../components/company/all-profiles/AllProfilesComponent";
import { RoleEnum } from "../../../../models/enums/RoleEnum";
import { Autocomplete, Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { GuidEnum } from "../../../../models/enums/GuidEnum";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { ScreenEnum } from "../../../../models/enums/StatusEnum";
import masterDataService from "../../../../services/master-data-service";

interface AdminAllProfilesProps {
    companiesList: IdNameModel[];
    primarySkillList: IdNameModel[];
};

const AdminAllProfilesComponent = (props: AdminAllProfilesProps) => {
    let loggedInUser = useAppSelector((state) => state.authState.loggedInUser);
    const [companySearch, setCompanySearch] = useState<string>(GuidEnum.Empty);
    const [startDateSearch, setStartDateSearch] = useState<Dayjs | null>(dayjs().subtract(15, 'day'));
    const [endDateSearch, setEndDateSearch] = useState<Dayjs | null>(dayjs());
    const [primarySKillSearch, setPrimarySkillSearch] = useState<number>(0);
    const [loadAllProfilesGrid, setLoadAllProfilesGrid] = useState<number>(2);
    const [ratedCandidatesSearch, setRatedCandidatesSearch] = useState<boolean>(false);
    const [statusList, setStatusList] = useState<IdNameModel[]>([]);
    const [refreshStatus, setRefreshStatus] = useState<number>(2);

    useEffect(() => {
        getStatusList();
        clearSearch();
    }, []);

    const clearSearch = () => {
        setStartDateSearch(dayjs().subtract(15, 'day'));
        setEndDateSearch(dayjs());
        setPrimarySkillSearch(0);
        setLoadAllProfilesGrid(2);
    };

    const getStatusList = () => {
        setRefreshStatus(2);
        masterDataService.loadStatusByScreen(ScreenEnum.AddProfileStatus).then((res) => {
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            setStatusList(res.items);
            setRefreshStatus(refreshStatus * 5);
        }).catch((error) => {
            setRefreshStatus(refreshStatus * 5);
            toasterUtils.showError(error);
        });
    };

    const searchAllProfiles = () => {
        if (startDateSearch === null || endDateSearch === null) {
            toasterUtils.showError("Please select Start Date and End Date");
            return;
        } else {
            setLoadAllProfilesGrid(loadAllProfilesGrid * 5);
        }
    };

    const handleRatedCandidatesSearch = (): void => {
        setRatedCandidatesSearch(!ratedCandidatesSearch);
    };

    return (
        <div>
            <div className="card card-white">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-3">
                            <FormControl fullWidth margin="none" size="small">
                                <Autocomplete options={props.companiesList} value={props.companiesList.find((x) => x.guId === companySearch) || null}
                                    onChange={(_, newValue) => setCompanySearch(newValue ? newValue.guId : GuidEnum.Empty)} getOptionLabel={(option) => option?.name || ""} isOptionEqualToValue={(option, value) => option.guId === value.guId}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Company" size="small" margin="normal" fullWidth required />
                                    )}
                                />
                            </FormControl>
                        </div>
                        <div className="col-md-3">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Start Date"
                                    value={startDateSearch}
                                    onChange={(newValue) => setStartDateSearch(newValue)}
                                    format="YYYY-MMM-DD"
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: "small",
                                            margin: "normal",
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </div>
                        <div className="col-md-3">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="End Date"
                                    value={endDateSearch}
                                    onChange={(newValue) => setEndDateSearch(newValue)}
                                    format="YYYY-MMM-DD"
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: "small",
                                            margin: "normal",
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </div>
                        <div className="col-md-3">
                            <FormControl fullWidth margin="none" size="small">
                                <Autocomplete options={props.primarySkillList} value={props.primarySkillList.find((x) => x.id === primarySKillSearch) || null}
                                    onChange={(_, newValue) => setPrimarySkillSearch(newValue ? newValue.id : 0)} getOptionLabel={(option) => option?.name || ""} isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Primary Skill" size="small" margin="normal" fullWidth />
                                    )}
                                />
                            </FormControl>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-3">
                            <FormControlLabel
                                control={
                                    <Checkbox checked={ratedCandidatesSearch} onChange={handleRatedCandidatesSearch} color="primary" />
                                }
                                label="Rated"
                            />
                        </div>
                        <div className="col-md-9 text-end btn-seach-clear">
                            <Button variant="contained" color="success" onClick={() => searchAllProfiles()} startIcon={<SearchIcon />} sx={{ mr: 2, mt: 2 }}>
                                Search
                            </Button>
                            <Button variant="contained" color="warning" onClick={() => clearSearch()} startIcon={<ClearIcon />} sx={{ mr: 2, mt: 2 }}>
                                Clear
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            {loadAllProfilesGrid > 2 && loggedInUser &&
                <AllProfilesComponent
                    key={loadAllProfilesGrid}
                    primarySkillList={props.primarySkillList}
                    statusList={statusList}
                    profileId={companySearch !== null && companySearch !== "" && companySearch !== GuidEnum.Empty ? [companySearch] : Number(loggedInUser.roleId) !== Number(RoleEnum.Administrator) ? props.companiesList.map(item => item.guId) : []}
                    userId={loggedInUser.userId}
                    startDateSearch={(startDateSearch ? startDateSearch.toDate() : new Date())}
                    endDateSearch={(endDateSearch ? endDateSearch.toDate() : new Date())}
                    primarySkillSearch={primarySKillSearch}
                    isRated={ratedCandidatesSearch} />
            }
        </div>
    );
};
export default AdminAllProfilesComponent;